import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginUser, register as registerUser } from '../services/UserService';
import { claimAnonSurvey } from '../services/SurveyService';
import { usePurchaseService } from '../services/PurchaseService';
import { getUserFacingErrorMessage } from '../services/errors';
import {
  PASSWORD_REQUIREMENTS_TEXT,
  validateEmail,
  validatePasswordStrength,
} from '../utils/validation';
import './NavBar.css';

const NavBar = () => {
  // Feature flag: keep auth UI code but hide it from the navbar for now.
  const SHOW_AUTH_UI = false;
  const { token, logout, login, userName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regName, setRegName] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regError, setRegError] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  // Mobile menu toggle
  const [menuOpen, setMenuOpen] = useState(false);
  const { initiateSurveyUnlock } = usePurchaseService();

  useEffect(() => {
    if (SHOW_AUTH_UI && !token && location.state?.openLogin) {
      setShowLogin(true);
      if (location.state.email) setLoginEmail(location.state.email);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, token, SHOW_AUTH_UI]);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  const setCookie = (name, value, hours) => {
    let expires = '';
    if (hours) {
      const date = new Date();
      date.setTime(date.getTime() + hours * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const data = await loginUser(loginEmail, loginPassword);
      setCookie('token', data.token, 1);
      login(data.token);
      // If there's an anon claim and we're on the result page, claim + purchase
      try {
        const stored = localStorage.getItem('anonClaim');
        if (stored && location.pathname === '/survey-result') {
          const claim = JSON.parse(stored);
          if (claim?.claimId) {
            // Show processing overlay in result page
            try {
              window.dispatchEvent(new CustomEvent('surveyProcessingStart'));
            } catch (evErr) {
              console.debug('surveyProcessingStart dispatch failed', evErr);
            }

            try {
              const claimed = await claimAnonSurvey(
                claim.claimId,
                claim.claimCode,
                data?.token,
              );
              try {
                localStorage.removeItem('anonClaim');
              } catch {}

              try {
                const surveyResponseId =
                  claimed.responseId ||
                  claimed.surveyResultId ||
                  claimed.survey_response_id ||
                  claimed.id;
                const purchaseResp = await initiateSurveyUnlock({
                  price: 19.95,
                  surveyResponseId,
                  claimId: claimed.claimId,
                });
                const hasSession =
                  purchaseResp?.sessionId ||
                  purchaseResp?.purchaseId ||
                  purchaseResp?.clientSecret;
                if (hasSession) {
                  setShowRegister(false);
                  setShowLogin(false);
                  setLoginEmail('');
                  setLoginPassword('');
                  setMenuOpen(false);
                  navigate('/payment', {
                    state: {
                      message:
                        purchaseResp?.message || 'Payment session created.',
                      purchaseId: purchaseResp?.purchaseId,
                      sessionId: purchaseResp?.sessionId,
                      clientSecret: purchaseResp?.clientSecret,
                      surveyResponseId:
                        purchaseResp?.surveyResponseId || surveyResponseId,
                    },
                  });
                  return;
                }

                // No session returned — show claimed result in place
                setShowRegister(false);
                setShowLogin(false);
                setLoginEmail('');
                setLoginPassword('');
                setMenuOpen(false);
                navigate(location.pathname, { state: { result: claimed } });
                return;
              } catch (payErr) {
                console.warn('Failed to initiate purchase after login', payErr);
                setShowRegister(false);
                setShowLogin(false);
                setLoginEmail('');
                setLoginPassword('');
                setMenuOpen(false);
                navigate(location.pathname, { state: { result: claimed } });
                return;
              } finally {
                try {
                  window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
                } catch (evErr) {}
              }
            } catch (claimErr) {
              console.warn('Failed to claim anon result after login', claimErr);
              try {
                window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
              } catch (evErr) {}
            }
          }
        }
      } catch (e) {
        console.warn('Error checking anonClaim after login', e);
      }

      setShowLogin(false);
      setLoginEmail('');
      setLoginPassword('');
      setMenuOpen(false);
      navigate('/');
    } catch (err) {
      // Prefer mapped errors; fallback keeps the legacy message.
      const msg = getUserFacingErrorMessage(err, 'login');
      setLoginError(msg || 'Invalid email or password');
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setRegError('');
    try {
      const emailCheck = validateEmail(regEmail);
      if (!emailCheck.ok) {
        setRegError(emailCheck.message);
        return;
      }

      const pwCheck = validatePasswordStrength(regPassword);
      if (!pwCheck.ok) {
        setRegError(pwCheck.message);
        return;
      }
      const data = await registerUser({
        email: emailCheck.normalized,
        name: regName,
        password: regPassword,
      });
      if (data?.token) {
        setCookie('token', data.token, 1);
        login(data.token);
      }

      // If there's an anon claim and we're on the result page, claim + purchase
      try {
        const stored = localStorage.getItem('anonClaim');
        if (stored && location.pathname === '/survey-result') {
          const claim = JSON.parse(stored);
          if (claim?.claimId) {
            // show processing overlay in result page
            try {
              window.dispatchEvent(new CustomEvent('surveyProcessingStart'));
            } catch (evErr) {
              console.debug('surveyProcessingStart dispatch failed', evErr);
            }

            try {
              const claimed = await claimAnonSurvey(
                claim.claimId,
                claim.claimCode,
                data?.token,
              );
              try {
                localStorage.removeItem('anonClaim');
              } catch {}

              try {
                const surveyResponseId =
                  claimed.responseId ||
                  claimed.surveyResultId ||
                  claimed.survey_response_id ||
                  claimed.id;
                const purchaseResp = await initiateSurveyUnlock({
                  price: 19.95,
                  surveyResponseId,
                  claimId: claimed.claimId,
                });
                const hasSession =
                  purchaseResp?.sessionId ||
                  purchaseResp?.purchaseId ||
                  purchaseResp?.clientSecret;
                if (hasSession) {
                  setShowRegister(false);
                  setShowLogin(false);
                  setRegEmail('');
                  setRegName('');
                  setRegPassword('');
                  navigate('/payment', {
                    state: {
                      message:
                        purchaseResp?.message || 'Payment session created.',
                      purchaseId: purchaseResp?.purchaseId,
                      sessionId: purchaseResp?.sessionId,
                      clientSecret: purchaseResp?.clientSecret,
                      surveyResponseId:
                        purchaseResp?.surveyResponseId || surveyResponseId,
                    },
                  });
                  return;
                }

                // No session returned — show claimed result
                setShowRegister(false);
                setShowLogin(false);
                setRegEmail('');
                setRegName('');
                setRegPassword('');
                navigate(location.pathname, { state: { result: claimed } });
                return;
              } catch (payErr) {
                console.warn(
                  'Failed to initiate purchase after register',
                  payErr,
                );
                setShowRegister(false);
                setShowLogin(false);
                setRegEmail('');
                setRegName('');
                setRegPassword('');
                navigate(location.pathname, { state: { result: claimed } });
                return;
              } finally {
                try {
                  window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
                } catch (evErr) {}
              }
            } catch (claimErr) {
              console.warn(
                'Failed to claim anon result after register',
                claimErr,
              );
              try {
                window.dispatchEvent(new CustomEvent('surveyProcessingEnd'));
              } catch (evErr) {}
            }
          }
        }
      } catch (e) {
        console.warn('Error checking anonClaim after register', e);
      }

      setShowRegister(false);
      setShowLogin(false);
      setRegEmail('');
      setRegName('');
      setRegPassword('');
      navigate('/');
    } catch (err) {
      setRegError(getUserFacingErrorMessage(err, 'register'));
    }
  };

  return (
    <>
      <nav className='nav'>
        <div className='nav__left'>
          <img
            src='/UpdatedInnerScienceLogo.png'
            alt='Logo'
            className='nav__logo'
          />
          <Link to='/'>The InnerScience Personality Test</Link>
        </div>
        <div className='nav__links'>
          {token ? (
            <>
              <span>Hello{userName ? `, ${userName}` : ''}</span>
              <Link to='/dashboard'>My Account</Link>
              <button className='btn btn--outline' onClick={handleLogout}>
                Logout
              </button>
            </>
          ) : (
            <>
              {SHOW_AUTH_UI && (
                <>
                  <button
                    className='btn btn--primary'
                    onClick={() => setShowLogin(true)}
                  >
                    Login
                  </button>
                  <button
                    className='btn btn--outline'
                    onClick={() => setShowRegister(true)}
                  >
                    Register
                  </button>
                </>
              )}
            </>
          )}
        </div>
        {/* Hamburger for small screens */}
        <button
          className='nav__toggle'
          type='button'
          aria-label='Toggle menu'
          aria-expanded={menuOpen}
          aria-controls='mobile-menu'
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span className='bar' />
          <span className='bar' />
          <span className='bar' />
        </button>
      </nav>

      {/* Mobile panel under navbar */}
      {menuOpen && (
        <div className='nav__mobile-panel' id='mobile-menu'>
          {token ? (
            <>
              <Link
                className='btn btn--outline'
                to='/dashboard'
                onClick={() => setMenuOpen(false)}
              >
                My Account
              </Link>
              <button
                className='btn btn--primary'
                type='button'
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              {SHOW_AUTH_UI && (
                <>
                  <button
                    className='btn btn--primary'
                    type='button'
                    onClick={() => {
                      setMenuOpen(false);
                      setShowLogin(true);
                    }}
                  >
                    Login
                  </button>
                  <button
                    className='btn btn--outline'
                    type='button'
                    onClick={() => {
                      setMenuOpen(false);
                      setShowRegister(true);
                    }}
                  >
                    Register
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}

      {SHOW_AUTH_UI && !token && showLogin && (
        <div className='modal-backdrop'>
          <div className='modal'>
            <button
              className='modal__close'
              aria-label='Close login'
              title='Close'
              type='button'
              onClick={() => setShowLogin(false)}
            >
              ×
            </button>
            <h3>Login</h3>
            <form onSubmit={submitLogin} className='form'>
              <input
                className='input'
                type='email'
                placeholder='Email'
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
              <div style={{ position: 'relative' }}>
                <input
                  className='input'
                  type={showLoginPassword ? 'text' : 'password'}
                  placeholder='Password'
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                  required
                />
                <button
                  type='button'
                  aria-label={
                    showLoginPassword ? 'Hide password' : 'Show password'
                  }
                  onClick={() => setShowLoginPassword((v) => !v)}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 6,
                    lineHeight: 1,
                    color: 'inherit',
                    opacity: 0.85,
                  }}
                >
                  {showLoginPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {loginError && <p className='form-error'>{loginError}</p>}
              <div className='modal__actions modal__actions--row'>
                <button
                  className='btn btn--outline'
                  type='button'
                  onClick={() => {
                    setShowLogin(false);
                    setShowRegister(true);
                    setRegEmail(loginEmail);
                  }}
                >
                  Register
                </button>
                <button
                  className='btn btn--outline'
                  type='button'
                  onClick={() => {
                    setShowLogin(false);
                    navigate('/forgot-password', {
                      state: { email: loginEmail },
                    });
                  }}
                >
                  Forgot Password?
                </button>
                <button className='btn btn--primary' type='submit'>
                  Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {SHOW_AUTH_UI && !token && showRegister && (
        <div className='modal-backdrop'>
          <div className='modal'>
            <button
              className='modal__close'
              aria-label='Close register'
              title='Close'
              type='button'
              onClick={() => setShowRegister(false)}
            >
              ×
            </button>
            <h3>Register</h3>
            <form onSubmit={submitRegister} className='form'>
              <input
                className='input'
                type='email'
                placeholder='Email'
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                required
              />
              <p className='form-disclaimer'>
                We respect your privacy. We will only use your email for account
                communication related to this service. We will not sell or share
                your email with third parties, and we will not send marketing
                emails without your explicit consent.
              </p>
              <input
                className='input'
                type='text'
                placeholder='Name'
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                required
              />
              <div style={{ position: 'relative' }}>
                <input
                  className='input'
                  type={showRegPassword ? 'text' : 'password'}
                  placeholder='Password'
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  style={{ paddingRight: 44 }}
                  required
                />
                <button
                  type='button'
                  aria-label={
                    showRegPassword ? 'Hide password' : 'Show password'
                  }
                  onClick={() => setShowRegPassword((v) => !v)}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 6,
                    lineHeight: 1,
                    color: 'inherit',
                    opacity: 0.85,
                  }}
                >
                  {showRegPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {regError !== PASSWORD_REQUIREMENTS_TEXT && (
                <p className='form-disclaimer'>{PASSWORD_REQUIREMENTS_TEXT}</p>
              )}
              {regError && <p className='form-error'>{regError}</p>}
              <div className='modal__actions modal__actions--row'>
                <button
                  className='btn btn--outline'
                  type='button'
                  onClick={() => {
                    setShowRegister(false);
                    setShowLogin(true);
                    setLoginEmail(regEmail);
                  }}
                >
                  Login
                </button>
                <button
                  className='btn btn--outline'
                  type='button'
                  onClick={() => {
                    setShowRegister(false);
                    navigate('/forgot-password', {
                      state: { email: regEmail },
                    });
                  }}
                >
                  Forgot Password?
                </button>
                <button className='btn btn--primary' type='submit'>
                  Register
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
